import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Avatar,
  Tooltip,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SourceIcon from '@mui/icons-material/Source';
import NoteIcon from '@mui/icons-material/Note';

interface LeadTooltipProps {
  lead: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    source: string;
    status: string;
    notes?: string;
    lastContactDate?: string;
    productInterest?: string;
  };
  product?: {
    id: string;
    name: string;
    price: number;
  } | null;
  children: React.ReactElement;
}

const statusColors: Record<string, "info" | "warning" | "primary" | "success" | "error"> = {
  "Novo": 'info',
  "Contatado": 'warning',
  "Qualificado": 'primary',
  "Convertido": 'success',
  "Perdido": 'error',
};

const LeadTooltip: React.FC<LeadTooltipProps> = ({ lead, product, children }) => {
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (error) {
      return '-';
    }
  };

  return (
    <Tooltip
      title={
        <Card sx={{ minWidth: 300, maxWidth: 350, boxShadow: 'none' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                {lead.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" component="div">
                  {lead.name}
                </Typography>
                <Chip 
                  label={lead.status} 
                  size="small" 
                  color={statusColors[lead.status] || 'default'} 
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
            
            <Divider sx={{ my: 1.5 }} />
            
            <Box sx={{ '& > div': { mb: 1 } }}>
              <Box display="flex" alignItems="center">
                <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{lead.email}</Typography>
              </Box>
              
              {lead.phone && (
                <Box display="flex" alignItems="center">
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{lead.phone}</Typography>
                </Box>
              )}
              
              <Box display="flex" alignItems="center">
                <SourceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">Origem: {lead.source}</Typography>
              </Box>
              
              {lead.lastContactDate && (
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">Último contato: {formatDate(lead.lastContactDate)}</Typography>
                </Box>
              )}
              
              {product && (
                <Box display="flex" alignItems="center" mt={1}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Produto de interesse:
                  </Typography>
                  <Link 
                    component={RouterLink} 
                    to={`/produtos/${product.id}`}
                    underline="hover"
                    color="primary"
                  >
                    {product.name} (R$ {product.price.toFixed(2)})
                  </Link>
                </Box>
              )}
              
              {lead.productInterest === 'automatic_later' && (
                <Box display="flex" alignItems="center" mt={1}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Produto de interesse:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Automático, vamos falar mais tarde
                  </Typography>
                </Box>
              )}
            </Box>
            
            {lead.notes && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" alignItems="flex-start">
                  <NoteIcon fontSize="small" sx={{ mr: 1, mt: 0.3, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {lead.notes.length > 100 
                      ? `${lead.notes.substring(0, 100)}...` 
                      : lead.notes}
                  </Typography>
                </Box>
              </>
            )}
            
            <Box mt={2} display="flex" justifyContent="center">
              <Typography variant="caption" color="text.secondary">
                Clique para ver perfil completo
              </Typography>
            </Box>
          </CardContent>
        </Card>
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 3,
            '& .MuiTooltip-arrow': {
              color: 'background.paper',
            },
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export default LeadTooltip; 